import { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout } from './components/Layout';
import { KPICards } from './components/KPICards';
import { MatrixMap } from './components/MatrixMap';
import { Filters } from './components/Filters';
import { OrdersTable } from './components/OrdersTable';
import { OrderDetail } from './components/OrderDetail';
import { OverrideEditor } from './components/OverrideEditor';
import { FunnelCard, SearchInsightsCard } from './components/Insights';
import { SessionPlayer } from './components/SessionPlayer';
import { AdminPanel } from './components/AdminPanel';
import { analytics, mapEventsToOrders, buildFunnel, buildSearchInsights, applyExclusions } from './lib/analytics';
import { analyticsCache } from './lib/cache';
import { adminStore } from './lib/admin';
import { HYDRATED_EVENT } from './lib/persistence';
import { db } from './lib/db';
import { auth } from './lib/auth';
import type { Order, FilterState, KPIData, User, AnalyticsEvent, OrderStatus } from './types';
import { Shield, Loader2, Plus } from 'lucide-react';

// Demo data for when the analytics API is unavailable
const DEMO_ORDERS: Order[] = [
  {
    id: 'ord_001',
    customerName: 'St. Mary\'s Hospital',
    customerEmail: 'procurement@stmarys.org',
    partNumbers: ['GE-MRI-750W', 'PHILIPS-CT-64'],
    total: 124500,
    status: 'unknown',
    createdAt: '2026-08-01T10:30:00Z',
    updatedAt: '2026-08-01T10:45:00Z',
    sessionStart: '2026-08-01T10:30:00Z',
    checkoutSuccess: '2026-08-01T10:45:00Z',
    abandoned: false,
    source: 'live',
  },
  {
    id: 'ord_002',
    customerName: 'Mercy General',
    customerEmail: 'orders@mercygeneral.com',
    partNumbers: ['SIEMENS-XRAY-DR'],
    total: 67800,
    status: 'unknown',
    createdAt: '2026-08-01T14:20:00Z',
    updatedAt: '2026-08-01T16:45:00Z',
    sessionStart: '2026-08-01T14:20:00Z',
    checkoutSuccess: '2026-08-01T16:45:00Z',
    abandoned: false,
    source: 'live',
  },
  {
    id: 'ord_003',
    customerName: 'University Medical Center',
    customerEmail: 'buyer@umc.edu',
    partNumbers: ['GE-ULTRASOUND-Vivid', 'HITACHI-MRI-1.5T'],
    total: 215000,
    status: 'unknown',
    createdAt: '2026-08-02T09:15:00Z',
    updatedAt: '2026-08-02T14:30:00Z',
    sessionStart: '2026-08-02T09:15:00Z',
    checkoutSuccess: '2026-08-02T14:30:00Z',
    abandoned: false,
    source: 'live',
  },
  {
    id: 'ord_004',
    customerName: 'Regional Health Partners',
    customerEmail: 'purchasing@rhp.net',
    partNumbers: ['PHILIPS-MRI-3T'],
    total: 350000,
    status: 'unknown',
    createdAt: '2026-08-02T11:00:00Z',
    updatedAt: '2026-08-02T11:12:00Z',
    sessionStart: '2026-08-02T11:00:00Z',
    checkoutSuccess: '2026-08-02T11:12:00Z',
    abandoned: false,
    source: 'live',
  },
  {
    id: 'ord_005',
    customerName: 'Children\'s Memorial',
    customerEmail: 'supply@childrensmem.org',
    partNumbers: ['TOSHIBA-CT-Aquilion'],
    total: 89000,
    status: 'unknown',
    createdAt: '2026-08-03T08:45:00Z',
    updatedAt: '2026-08-03T08:58:00Z',
    sessionStart: '2026-08-03T08:45:00Z',
    checkoutSuccess: '2026-08-03T08:58:00Z',
    abandoned: false,
    source: 'live',
  },
  {
    id: 'ord_006',
    customerName: 'Coastal Medical Group',
    customerEmail: 'admin@coastalmed.com',
    partNumbers: ['GE-CT-Revolution'],
    total: 0,
    status: 'unknown',
    createdAt: '2026-08-03T13:20:00Z',
    updatedAt: '2026-08-03T13:20:00Z',
    sessionStart: '2026-08-03T13:20:00Z',
    abandoned: true,
    source: 'live',
  },
  {
    id: 'ord_007',
    customerName: 'Pacific Imaging',
    customerEmail: 'orders@pacificimaging.com',
    partNumbers: ['SIEMENS-MRI-Magnetom', 'PHILIPS-XRAY-Digital'],
    total: 180000,
    status: 'unknown',
    createdAt: '2026-08-04T10:00:00Z',
    updatedAt: '2026-08-04T11:30:00Z',
    sessionStart: '2026-08-04T10:00:00Z',
    checkoutSuccess: '2026-08-04T11:30:00Z',
    abandoned: false,
    source: 'live',
  },
  {
    id: 'ord_008',
    customerName: 'Metro Healthcare',
    customerEmail: 'buyers@metrohealth.org',
    partNumbers: ['HITACHI-CT-Supria'],
    total: 0,
    status: 'unknown',
    createdAt: '2026-08-04T15:30:00Z',
    updatedAt: '2026-08-04T15:30:00Z',
    sessionStart: '2026-08-04T15:30:00Z',
    abandoned: true,
    source: 'live',
  },
  {
    id: 'ord_009',
    customerName: 'North Valley Hospital',
    customerEmail: 'nvh@northvalley.org',
    partNumbers: ['GE-MRI-3T-Signa'],
    total: 425000,
    status: 'unknown',
    createdAt: '2026-08-05T09:00:00Z',
    updatedAt: '2026-08-05T09:08:00Z',
    sessionStart: '2026-08-05T09:00:00Z',
    checkoutSuccess: '2026-08-05T09:08:00Z',
    abandoned: false,
    source: 'live',
  },
  {
    id: 'ord_010',
    customerName: 'South Bay Medical',
    customerEmail: 'purchasing@southbay.med',
    partNumbers: ['PHILIPS-MRI-Ingenia'],
    total: 295000,
    status: 'unknown',
    createdAt: '2026-08-05T11:15:00Z',
    updatedAt: '2026-08-05T16:00:00Z',
    sessionStart: '2026-08-05T11:15:00Z',
    checkoutSuccess: '2026-08-05T16:00:00Z',
    abandoned: false,
    source: 'live',
  },
];

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    dateFrom: '',
    dateTo: '',
    status: 'all',
    customerSearch: '',
    minAmount: '',
    maxAmount: '',
  });
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [viewMode, setViewMode] = useState<'dashboard' | 'orders'>('dashboard');
  const [dataSource, setDataSource] = useState<'live' | 'cached' | 'demo' | 'loading'>('loading');
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);
  const [lastSynced, setLastSynced] = useState<string | null>(null);
  const [impersonating, setImpersonating] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showAddOrder, setShowAddOrder] = useState(false);
  // Bumped whenever local admin data changes (aliases, exclusions, overrides,
  // deleted events) so all derived views recompute without a network call.
  const [adminVersion, setAdminVersion] = useState(0);
  const bumpAdminVersion = useCallback(() => setAdminVersion(v => v + 1), []);

  const isAdmin = !!user?.isAdmin && !impersonating;

  // Auth check
  useEffect(() => {
    const stored = auth.getUser();
    if (stored) {
      setUser(stored);
    } else {
      // For dev: auto-login as admin
      const devUser = auth.mockLogin('admin@oemstream.com', true);
      setUser(devUser);
    }
  }, []);

  // Fetch orders: render from the local cache instantly, then sync only the
  // delta (events newer than our local cursor) and merge it into the cache.
  const fetchOrders = useCallback(async () => {
    setError(null);

    // 1) Instant render from the local event store — no network wait.
    const cachedEvents = analyticsCache.loadEvents();
    setLastSynced(analyticsCache.getLastSync());
    if (cachedEvents.length > 0) {
      setEvents(cachedEvents);
      setDataSource('cached');
      setLoading(false);
    } else {
      setLoading(true);
      setDataSource('loading');
    }

    // 2) Incremental sync: fetch only events newer than what we already have.
    try {
      const cursor = analyticsCache.getSyncCursor();
      const after = cursor ?? (filters.dateFrom ? new Date(filters.dateFrom).toISOString() : undefined);
      const fresh = await analytics.fetchEventsSince(after);
      const mergedEvents = analyticsCache.mergeNew(fresh);
      setEvents(mergedEvents);
      setLastSynced(analyticsCache.getLastSync());
      setDataSource('live');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.warn('Analytics sync failed:', err);
      if (cachedEvents.length > 0) {
        // Cache keeps the dashboard fully usable while offline.
        setError(`Sync failed — ${message} Showing locally cached data.`);
        setDataSource('cached');
      } else {
        setEvents([]);
        setDataSource('demo');
        setError(`Live data unavailable — ${message} Showing demo data instead.`);
      }
    } finally {
      setLoading(false);
    }
  }, [filters.dateFrom]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Another admin's changes arrived from the shared server state — re-read
  // the local stores and recompute everything.
  useEffect(() => {
    const onHydrated = () => {
      setEvents(analyticsCache.loadEvents());
      setLastSynced(analyticsCache.getLastSync());
      bumpAdminVersion();
    };
    window.addEventListener(HYDRATED_EVENT, onHydrated);
    return () => window.removeEventListener(HYDRATED_EVENT, onHydrated);
  }, [bumpAdminVersion]);

  // ── Derived data: admin configuration applies globally on top of events ──
  // adminVersion is the invalidation signal: bumping it re-reads local admin
  // data (aliases, exclusions, overrides, deleted events) without a refetch.

  const aliases = useMemo(() => {
    void adminVersion;
    return adminStore.getAliases();
  }, [adminVersion]);

  const visibleEvents = useMemo(() => {
    void adminVersion;
    return applyExclusions(events, adminStore.getExclusions(), adminStore.getDeletedEventIds());
  }, [events, adminVersion]);

  const orders = useMemo(() => {
    void adminVersion;
    if (dataSource === 'demo') return db.mergeOrders(DEMO_ORDERS);
    return db.mergeOrders(mapEventsToOrders(visibleEvents, aliases));
  }, [dataSource, visibleEvents, aliases, adminVersion]);

  // Apply filters (dates are filtered client-side against the local cache)
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      if (filters.dateFrom && order.createdAt < new Date(filters.dateFrom).toISOString()) return false;
      if (filters.dateTo) {
        const end = new Date(filters.dateTo);
        end.setDate(end.getDate() + 1);
        if (order.createdAt >= end.toISOString()) return false;
      }
      if (filters.status !== 'all' && order.status !== filters.status) return false;
      if (filters.customerSearch) {
        const q = filters.customerSearch.toLowerCase();
        const haystack = [
          order.customerName,
          order.customerEmail,
          order.poNumber,
          order.incrementId,
          ...(order.partNumbers || []),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (filters.minAmount !== '' && order.total < Number(filters.minAmount)) return false;
      if (filters.maxAmount !== '' && order.total > Number(filters.maxAmount)) return false;
      return true;
    });
  }, [orders, filters]);

  // Insights derived from the filtered local event store
  const funnelData = useMemo(() => buildFunnel(visibleEvents), [visibleEvents]);
  const searchInsights = useMemo(() => buildSearchInsights(visibleEvents), [visibleEvents]);

  // Calculate KPIs
  const kpiData: KPIData = useMemo(() => {
    const completed = filteredOrders.filter(o => !o.abandoned);
    const abandoned = filteredOrders.filter(o => o.abandoned);
    
    const closeTimes = completed
      .map(o => o.checkoutSuccess && o.sessionStart
        ? new Date(o.checkoutSuccess).getTime() - new Date(o.sessionStart).getTime()
        : null
      )
      .filter((t): t is number => t !== null);
    
    const avgTime = closeTimes.length > 0
      ? closeTimes.reduce((a, b) => a + b, 0) / closeTimes.length
      : 0;

    const statusBreakdown: Record<OrderStatus, number> = {
      unknown: completed.filter(o => o.status === 'unknown').length,
      automation: completed.filter(o => o.status === 'automation').length,
      intervention: completed.filter(o => o.status === 'intervention').length,
      escalation: completed.filter(o => o.status === 'escalation').length,
    };

    return {
      totalTransactions: completed.length,
      avgTimeToClose: avgTime,
      abandonedCarts: abandoned.length,
      statusBreakdown,
    };
  }, [filteredOrders]);

  const handleDelete = (orderId: string) => {
    if (confirm('Are you sure you want to remove this order from the report?')) {
      db.deleteOrder(orderId);
      bumpAdminVersion();
    }
  };

  const handleSaveOverride = () => {
    bumpAdminVersion();
  };

  // Dev login for testing
  const handleDevLogin = (email: string, isAdmin: boolean) => {
    const u = auth.mockLogin(email, isAdmin);
    setUser(u);
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-oem-50">
        <div className="oem-card p-8 w-full max-w-md space-y-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-oem-navy rounded-xl flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-display text-xl">OS</span>
            </div>
            <h1 className="font-display text-2xl text-oem-navy">OEMSTREAM Dashboard</h1>
            <p className="text-sm text-oem-muted mt-1">Sign in to access KPI reports</p>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => handleDevLogin('admin@oemstream.com', true)}
              className="w-full oem-button flex items-center justify-center gap-2"
            >
              <Shield className="w-4 h-4" />
              Login as Admin (@oemstream.com)
            </button>
            <button
              onClick={() => handleDevLogin('user@example.com', false)}
              className="w-full oem-button-outline"
            >
              Login as Viewer
            </button>
          </div>

          <p className="text-xs text-center text-oem-muted">
            In production, authentication is handled by Cloudflare Access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Layout
      user={user}
      onRefresh={fetchOrders}
      lastSynced={lastSynced}
      isAdmin={isAdmin}
      impersonating={impersonating}
      onToggleImpersonate={() => setImpersonating(v => !v)}
      onOpenAdminPanel={() => setShowAdminPanel(true)}
    >
      {/* Hero */}
      <section className="hero relative overflow-hidden rounded-2xl px-8 py-10 sm:px-12 sm:py-12">
        <div className="relative">
          <span className="hero-tag">
            {dataSource === 'live' ? 'Live Analytics' : dataSource === 'cached' ? 'Local Cache' : dataSource === 'loading' ? 'Connecting…' : 'Demo Data'}
          </span>
          <h1 className="font-display text-white text-4xl sm:text-5xl leading-tight">
            Order Performance <em className="text-oem-300">Dashboard</em>
          </h1>
          <div className="flex flex-wrap gap-x-8 gap-y-2 mt-6">
            <span className="hero-meta-item">
              <span className="dot" />
              {dataSource === 'live'
                ? 'Source: live analytics (synced)'
                : dataSource === 'cached'
                  ? 'Source: local cache (offline-ready)'
                  : dataSource === 'loading'
                    ? 'Source: connecting…'
                    : 'Source: demo dataset'}
            </span>
            <span className="hero-meta-item">
              <span className="dot" />
              {lastSynced ? `Last synced ${new Date(lastSynced).toLocaleTimeString()}` : 'Not synced yet'}
            </span>
            <span className="hero-meta-item">
              <span className="dot" />
              {orders.length} orders · {events.length} events cached
            </span>
          </div>
        </div>
      </section>

      {error && (
        <div className="p-4 bg-status-intervention/10 border border-status-intervention/20 rounded-xl text-sm text-status-intervention">
          {error}
        </div>
      )}

      {loading ? (
        <div className="oem-card p-16 flex flex-col items-center justify-center gap-3 text-oem-muted">
          <Loader2 className="w-8 h-8 animate-spin text-accent-500" />
          <p className="text-sm">Loading order data…</p>
        </div>
      ) : (
        <>
          {/* View Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('dashboard')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'dashboard'
                    ? 'bg-oem-navy text-white'
                    : 'bg-white text-oem-600 border border-oem-200 hover:bg-oem-100'
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => setViewMode('orders')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'orders'
                    ? 'bg-oem-navy text-white'
                    : 'bg-white text-oem-600 border border-oem-200 hover:bg-oem-100'
                }`}
              >
                All Orders
              </button>
            </div>
            {isAdmin && (
              <button
                onClick={() => setShowAddOrder(true)}
                className="oem-button flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" />
                Add Order
              </button>
            )}
          </div>

          {/* KPI Cards */}
          <KPICards data={kpiData} />

          {/* Filters */}
          <Filters filters={filters} onChange={setFilters} />

          {/* Matrix Map */}
          <MatrixMap orders={filteredOrders} />

          {/* Funnel + admin-only Search Intelligence / Selected Session */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <FunnelCard funnel={funnelData} />
            {isAdmin ? (
              <SearchInsightsCard searches={searchInsights} />
            ) : (
              <SessionPlayer isAdmin={false} />
            )}
          </div>

          {/* Admins also manage the selected session (upload, title, notes) */}
          {isAdmin && <SessionPlayer isAdmin={true} />}

          {/* Orders Table */}
          <OrdersTable
            orders={filteredOrders}
            isAdmin={isAdmin}
            onView={setSelectedOrder}
            onEdit={setEditingOrder}
            onDelete={handleDelete}
          />
        </>
      )}

      {/* Modals */}
      {selectedOrder && (
        <OrderDetail order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}
      {editingOrder && (
        <OverrideEditor
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onSave={handleSaveOverride}
        />
      )}
      {showAddOrder && (
        <OverrideEditor
          order={null}
          onClose={() => setShowAddOrder(false)}
          onSave={handleSaveOverride}
        />
      )}
      {showAdminPanel && isAdmin && (
        <AdminPanel
          events={events}
          onClose={() => setShowAdminPanel(false)}
          onChanged={bumpAdminVersion}
        />
      )}
    </Layout>
  );
}

export default App;
